import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react"
import { Check, ChevronDown, CircleX, X } from "lucide-react"
import { twMerge } from "tailwind-merge"
import { motion, AnimatePresence } from "framer-motion"

import useClickOutside from "@src/hooks/useClickOutside"
import LoadingSpinner from "./LoadingSpinner"
import { createId } from "@src/utils"
import Input from "./Input"
import Text from "./Text"
import Required from "./Required"

interface MultiSelectSearchProps<K> {
  label?: string
  error?: string
  required?: boolean
  placeholder?: string
  options: K[]
  selected: K[]
  onChange: (selected: K[]) => void
  renderLabel: (item: K) => string
  renderChip?: (item: K) => string
  getValue: (item: K) => string | number
  children?: (item: K, index: number) => React.ReactNode
  maxSelections?: number
  listItemSize?: string
  className?: string
  classNameInput?: string
  classNameOption?: string
  loading?: boolean
  disabled?: boolean
  showClearAll?: boolean
  position?: "to-top" | "to-bottom"
  searchOffline?: boolean
  id?: string
  maxLength?: number
  viewInputSearchOffline?: boolean
}

interface MultiSelectSearchContextValue<K> {
  open: boolean
  toggle: React.Dispatch<React.SetStateAction<boolean>>
  onToggle: (item: K) => void
  isSelected: (item: K) => boolean
}

const MultiSelectSearchContext = createContext<MultiSelectSearchContextValue<any>>(
  {} as MultiSelectSearchContextValue<any>,
)

interface OptionProps<K> {
  className?: string
  children: React.ReactNode
  data: K
  isSelected?: boolean
}

const Option = React.memo(function Option<K>({
  data,
  children,
  className,
  isSelected = false,
  ...props
}: OptionProps<K>) {
  const { onToggle } = useContext(MultiSelectSearchContext)

  const handleClick = useCallback(() => {
    onToggle(data)
  }, [data, onToggle])

  return (
    <button
      type="button"
      className={twMerge(
        "flex-1 w-full bg-white enabled:hover:bg-gray-100 p-3 text-sm text-start pointer-events-auto dark:bg-legacy-navy dark:text-legacy-cream dark:hover:bg-legacy-light/20 transition-colors duration-150",
        // isSelected && "!bg-legacy-blue/10 dark:!bg-legacy-blue/20",
        className,
      )}
      onClick={handleClick}
      {...props}
    >
      <div className="flex items-center gap-2 w-full">
        <div className="flex-1 truncate">{children}</div>
        {isSelected && <Check size={16} className="text-legacy-blue dark:text-legacy-cream flex-shrink-0" />}
      </div>
    </button>
  )
}) as <K>(props: OptionProps<K>) => React.ReactElement

export function MultiSelectSearch<K>({
  label,
  error,
  required = false,
  placeholder = "Search...",
  options = [],
  selected = [],
  onChange,
  renderLabel,
  renderChip,
  getValue,
  children,
  maxSelections,
  listItemSize = "max-h-[250px]",
  className,
  classNameInput,
  classNameOption = "",
  loading = false,
  disabled = false,
  showClearAll = true,
  position = "to-bottom",
  searchOffline = true,
  id = createId(),
  maxLength = 255,
  viewInputSearchOffline = true,
}: MultiSelectSearchProps<K>) {
  const [open, setOpen] = useState<boolean>(false)
  const [search, setSearch] = useState<string>("")
  const [inputSearchOffline, setInputSearchOffline] = useState<string>("")
  const containerRef = useRef<HTMLDivElement | null>(null)
  const inputWrapperRef = useRef<HTMLDivElement | null>(null)
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 })
  const inputSearchOfflineRef = useRef<HTMLInputElement | null>(null)

  const selectedValuesSet = useMemo(() => {
    return new Set(selected.map((item) => getValue(item)))
  }, [selected, getValue])

  const filteredOptions = useMemo(() => {
    const searchTerm = (searchOffline ? inputSearchOffline : search).trim().toLowerCase()

    if (!searchTerm) {
      return options
    }

    return options.filter((item) => {
      const label = renderLabel(item).toLowerCase()
      return label.includes(searchTerm)
    })
  }, [options, search, inputSearchOffline, searchOffline, renderLabel])

  const updateDropdownPosition = useCallback(() => {
    if (open && containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect()
      const dropdownHeight = 250
      const top = position === "to-top" ? rect.top - dropdownHeight : rect.bottom

      setDropdownPosition({
        top,
        left: rect.left,
        width: rect.width,
      })
    }
  }, [open, position])

  useEffect(() => {
    updateDropdownPosition()
  }, [open, filteredOptions.length, position, updateDropdownPosition])

  useEffect(() => {
    if (open) {
      requestAnimationFrame(() => {
        updateDropdownPosition()
      })
    }
  }, [selected.length, open, updateDropdownPosition])

  useEffect(() => {
    if (!open || !containerRef.current) return

    const resizeObserver = new ResizeObserver(() => {
      updateDropdownPosition()
    })

    resizeObserver.observe(containerRef.current)

    return () => {
      resizeObserver.disconnect()
    }
  }, [open, updateDropdownPosition])

  useEffect(() => {
    if (!open) return

    const handleResize = () => {
      updateDropdownPosition()
    }

    const handleScroll = () => {
      updateDropdownPosition()
    }

    window.addEventListener("resize", handleResize)
    window.addEventListener("scroll", handleScroll, true)
    return () => {
      window.removeEventListener("resize", handleResize)
      window.removeEventListener("scroll", handleScroll, true)
    }
  }, [open, updateDropdownPosition])

  useClickOutside(containerRef, () => {
    if (open) {
      setOpen(false)
      setInputSearchOffline("")
      setSearch("")
    }
  })

  const onToggle = useCallback(
    (item: K) => {
      const itemValue = getValue(item)
      const isCurrentlySelected = selectedValuesSet.has(itemValue)

      if (isCurrentlySelected) {
        onChange(selected.filter((selectedItem) => getValue(selectedItem) !== itemValue))
      } else {
        if (maxSelections && selected.length >= maxSelections) {
          return
        }
        onChange([...selected, item])
        setTimeout(() => {
          inputSearchOfflineRef.current?.focus()
        }, 10)
      }
    },
    [selected, selectedValuesSet, onChange, getValue, maxSelections],
  )

  const isSelected = useCallback(
    (item: K) => {
      return selectedValuesSet.has(getValue(item))
    },
    [selectedValuesSet, getValue],
  )

  const removeItem = useCallback(
    (item: K) => {
      const itemValue = getValue(item)
      onChange(selected.filter((selectedItem) => getValue(selectedItem) !== itemValue))
    },
    [selected, onChange, getValue],
  )

  const clearAll = useCallback(() => {
    onChange([])
  }, [onChange])

  const handleKeyDown = useCallback((ev: React.KeyboardEvent<HTMLInputElement>) => {
    if (ev.key === "Escape" || ev.key === "Enter") {
      ev.stopPropagation()
      ev.preventDefault()
    }
    if (ev.key === "Escape") {
      setInputSearchOffline("")
      setOpen(false)
    }
  }, [])

  const getChipText = useCallback(
    (item: K) => {
      return renderChip ? renderChip(item) : renderLabel(item)
    },
    [renderChip, renderLabel],
  )

  const cssListItemSizeClasses = useMemo(
    () =>
      twMerge(
        "flex flex-col border rounded-lg overflow-y-auto scroll__styled shadow-lg dark:border-legacy-light/30",
        listItemSize,
      ),
    [listItemSize],
  )

  const contextValue = useMemo(
    () => ({
      open,
      toggle: setOpen,
      onToggle,
      isSelected,
    }),
    [open, onToggle, isSelected],
  )

  return (
    <>
      {!!label && (
        <Text as="label" color="legacy-dark" className="mb-0" htmlFor={id}>
          {label} {required && <Required />}
        </Text>
      )}
      {selected.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-2">
          {selected.map((item) => (
            <motion.div
              key={String(getValue(item))}
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.8 }}
              className="inline-flex items-center gap-1 px-2 py-1 bg-legacy-blue/10 dark:bg-legacy-blue/20 text-legacy-dark dark:text-legacy-cream rounded-md text-sm border border-legacy-blue/20 dark:border-legacy-blue/30"
            >
              <span className="truncate max-w-[200px]">{getChipText(item)}</span>
              {!disabled && (
                <button
                  type="button"
                  onClick={() => removeItem(item)}
                  className="flex-shrink-0 p-0.5 hover:bg-legacy-blue/20 dark:hover:bg-legacy-blue/30 rounded transition-colors"
                  aria-label="Remove item"
                >
                  <X size={14} className="text-legacy-dark dark:text-legacy-cream" />
                </button>
              )}
            </motion.div>
          ))}
          {showClearAll && selected.length > 1 && !disabled && (
            <button
              type="button"
              onClick={clearAll}
              className="text-xs text-red-600 dark:text-red-400 hover:underline px-2 py-1"
            >
              Clear all
            </button>
          )}
        </div>
      )}
      <div className={twMerge("flex flex-col w-full", className)}>
        <div className={twMerge("w-full h-full", open ? "relative" : "")} ref={containerRef}>
          <MultiSelectSearchContext.Provider value={contextValue}>
            <div className="flex flex-col gap-2" ref={inputWrapperRef}>
              <div className="group/item relative flex-1">
                <Input
                  onFocus={() => setOpen(true)}
                  placeholder={placeholder}
                  value={searchOffline ? "" : search}
                  onChange={(ev) => setSearch(ev.target.value)}
                  onKeyDown={handleKeyDown}
                  autoComplete="off"
                  maxLength={maxLength}
                  readOnly={searchOffline}
                  errorStyled={!!error}
                  disabled={disabled}
                  id={id}
                  className={twMerge("cursor-pointer", classNameInput)}
                />
                <div className="pointer-events-none absolute inset-y-0 right-1 flex items-center px-0.5 text-gray-400 gap-0.5">
                  {!disabled && showClearAll && selected.length > 0 && (
                    <CircleX
                      strokeWidth={2.25}
                      className={twMerge(
                        "invisible text-sm relative pointer-events-auto select-none text-red-600 dark:text-red-400 cursor-pointer transition-opacity",
                        selected.length > 0 && "group-hover/item:visible",
                      )}
                      onClick={clearAll}
                      size={14}
                    />
                  )}
                  {loading && (
                    <div className="-translate-x-1 leading-none">
                      <LoadingSpinner size="sm" />
                    </div>
                  )}
                  <ChevronDown
                    className={twMerge("transition-transform duration-300", open && "transform rotate-180")}
                    size={14}
                  />
                </div>
              </div>

              {/* Error message */}
              {error && (
                <motion.p
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="text-sm text-red-600 dark:text-red-400"
                >
                  {error}
                </motion.p>
              )}

              {/* Dropdown */}
              <AnimatePresence>
                {open && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 10 }}
                    transition={{ duration: 0.2 }}
                    className={twMerge(cssListItemSizeClasses, "fixed z-[1051] bg-white dark:bg-legacy-navy")}
                    style={{
                      top: `${dropdownPosition.top + 4}px`,
                      left: `${dropdownPosition.left}px`,
                      width: `${dropdownPosition.width}px`,
                    }}
                  >
                    {/* Search input in dropdown */}
                    {searchOffline && viewInputSearchOffline && (
                      <div className="sticky top-0 w-full p-0.5 pt-1 px-1 first-of-type:rounded-t last-of-type:rounded-b bg-white dark:bg-legacy-navy border-b border-legacy-light/20 dark:border-legacy-light/10">
                        <Input
                          ref={inputSearchOfflineRef}
                          placeholder="Search..."
                          className="w-full! focus:ring"
                          autoFocus
                          value={inputSearchOffline}
                          onKeyDown={handleKeyDown}
                          onChange={(ev) => setInputSearchOffline(ev.target.value)}
                        />
                      </div>
                    )}

                    {/* Options list */}
                    {filteredOptions.length > 0 ? (
                      <>
                        {maxSelections && selected.length >= maxSelections && (
                          <div className="px-3 py-2 text-xs text-yellow-600 dark:text-yellow-400 bg-yellow-50 dark:bg-yellow-900/20 border-b border-yellow-200 dark:border-yellow-800">
                            Maximum {maxSelections} selection{maxSelections > 1 ? "s" : ""} reached
                          </div>
                        )}
                        {filteredOptions.map((option, index) => {
                          const optionIsSelected = isSelected(option)
                          const isDisabled =
                            maxSelections && selected.length >= maxSelections && !optionIsSelected

                          if (children) {
                            return (
                              <Option
                                key={String(getValue(option))}
                                data={option}
                                isSelected={optionIsSelected}
                                className={twMerge(
                                  classNameOption,
                                  isDisabled && "opacity-50 cursor-not-allowed",
                                )}
                              >
                                {children(option, index)}
                              </Option>
                            )
                          }

                          return (
                            <Option
                              key={String(getValue(option))}
                              data={option}
                              isSelected={optionIsSelected}
                              className={twMerge(
                                classNameOption,
                                isDisabled && "opacity-50 cursor-not-allowed",
                              )}
                            >
                              {renderLabel(option)}
                            </Option>
                          )
                        })}
                      </>
                    ) : (
                      <div className="flex bg-white dark:bg-legacy-navy items-center justify-center h-full p-3">
                        <span className="text-xs text-legacy-light dark:text-legacy-light/70">
                          {search || inputSearchOffline ? "No results found" : "No options available"}
                        </span>
                      </div>
                    )}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </MultiSelectSearchContext.Provider>
        </div>
      </div>
    </>
  )
}

// Attach Option as static property
MultiSelectSearch.Option = Option

export default MultiSelectSearch
